/**
 * Brinde Automático – Auto Gift System v5
 *
 * Fetch interception + event listeners + periodic polling fallback.
 * Triggered on any /cart/add.js, /cart/update.js, /cart/change.js call.
 */
(function () {
  'use strict';

  const BRINDE_KEY = '_brinde_auto';
  const BRINDE_VAL = 'true';

  /* ── Global lock: prevents brinde from running during cart ops ── */
  window.__cartBusy = false;

  class BrindeAutoManager {
    constructor(config) {
      this.enabled = config.enabled;
      this.mode    = config.mode;
      this.tiers   = config.tiers
        .filter(t => t.enabled && t.variantId && t.minValue > 0)
        .sort((a, b) => a.minValue - b.minValue);

      this.processing    = false;
      this.debounceTimer = null;
      this._backoffUntil = 0;

      if (this.enabled && this.tiers.length > 0) {
        this._init();
      }
    }

    _init() {
      // Initial check — 1 second after load
      setTimeout(() => this._schedule(500), 1000);

      // Listen for theme cart events (universal fallback)
      this._listenCartEvents();

      // Intercept fetch to catch /cart/add.js, /cart/update.js, /cart/change.js
      this._interceptFetch();

      // NOTE: Periodic polling removed — it causes rate limiting (429) that
      // blocks the entire Shopify cart API for the session. The fetch
      // interceptor + cart event listeners above already cover all cart changes.
    }

    _listenCartEvents() {
      const trigger = () => this._schedule(1500);
      [
        'cart:updated', 'cart:add', 'cart:change', 'cart:refresh',
        'bls:cart:updated', 'on:cart:update', 'theme:cart:update'
      ].forEach(evt => document.addEventListener(evt, trigger));
    }

    _interceptFetch() {
      const self = this;
      const _origFetch = window.fetch;
      // Guard: some environments (Shopify CLI dev tools) make window.fetch
      // non-writable. Wrap in try/catch so the constructor doesn't throw.
      try {
        window.fetch = function(...args) {
          const url = (typeof args[0] === 'string' ? args[0] : (args[0]?.url || ''));
          const isCartOp = /\/cart\/(add|update|change)\.js/.test(url);
          const promise = _origFetch.apply(this, args);
          if (isCartOp) {
            promise.then(() => {
              // Delay to let the theme update its UI first
              self._schedule(1200);
            }).catch(() => {});
          }
          return promise;
        };
      } catch (e) {
        // window.fetch is read-only (e.g. Shopify analytics script ran first).
        // Cart event listeners (_listenCartEvents) already cover this case.
        console.log('[BrindeAuto] fetch interception unavailable, relying on cart events only');
      }
    }

    _schedule(delay = 1500) {
      clearTimeout(this.debounceTimer);
      // Invalidate cart cache so the next check reads fresh data
      window.__brindeCartPromise = null;
      console.log('[BrindeAuto] Scheduled check in', delay, 'ms');
      this.debounceTimer = setTimeout(() => this._runCheck(), delay);
    }

    async _runCheck() {
      // Don't run if in backoff period (after 429)
      if (Date.now() < this._backoffUntil) {
        console.log('[BrindeAuto] Skipped: rate limit backoff active');
        return;
      }
      // Don't run if a cart operation is in progress
      if (this.processing || window.__cartBusy) {
        console.log('[BrindeAuto] Skipped: processing=', this.processing, '__cartBusy=', window.__cartBusy);
        return;
      }
      this.processing = true;
      console.log('[BrindeAuto] Running check...');

      try {
        // Wait a beat to make sure any pending cart ops finish
        await new Promise(r => setTimeout(r, 500));
        if (window.__cartBusy) {
          console.log('[BrindeAuto] Aborted: __cartBusy became true during wait');
          this.processing = false;
          return;
        }

        const cart = await this._fetchCart();
        if (!cart) {
          console.log('[BrindeAuto] Failed to fetch cart');
          // Backoff 30s on repeated failures to avoid rate limit loop
          this._backoffUntil = Date.now() + 30000;
          this.processing = false;
          return;
        }

        const currentGiftItems = cart.items.filter(i => i.properties?.[BRINDE_KEY] === BRINDE_VAL);

        /* ── Itens "irregulares": produto-brinde no carrinho SEM property
              _brinde_auto. Significa que o cliente conseguiu adicionar o
              produto-brinde fora do fluxo (página do produto, app, link direto,
              quick add, etc). Esses devem ser removidos imediatamente para
              impedir que ele finalize a compra só com o item gratuito. */
        const giftProductIds = new Set(
          this.tiers.map(t => t.productId).filter(Boolean)
        );
        const giftVariantIds = new Set(
          this.tiers.filter(t => !t.productId).map(t => t.variantId)
        );
        const illegalGiftItems = cart.items.filter(i => {
          if (i.properties?.[BRINDE_KEY] === BRINDE_VAL) return false; // já é brinde regular
          if (giftProductIds.has(i.product_id)) return true;
          if (giftVariantIds.has(i.variant_id)) return true;
          return false;
        });

        if (illegalGiftItems.length > 0) {
          console.log('[BrindeAuto] Removing illegal gift items (added outside flow):', illegalGiftItems.length);
          const updates = {};
          illegalGiftItems.forEach(i => { updates[i.key] = 0; });
          await this._cartRequest('/cart/update.js', { updates });
          await new Promise(r => setTimeout(r, 400));
          // Reagenda o check com o carrinho limpo (sem refazer toda a lógica aqui)
          window.__brindeCartPromise = null;
          this.processing = false;
          this._schedule(600);
          return;
        }

        const giftKeys = new Set(currentGiftItems.map(i => i.key));
        const subtotal = cart.items.reduce((sum, i) => giftKeys.has(i.key) ? sum : sum + i.final_line_price, 0);

        console.log('[BrindeAuto] Subtotal (cents):', subtotal, '| Items:', cart.items.length, '| Gifts in cart:', currentGiftItems.length);

        const qualifiedTiers = this.tiers.filter(t => subtotal >= t.minValue);
        const targetTiers = !qualifiedTiers.length ? [] :
          this.mode === 'replace'
            ? [qualifiedTiers[qualifiedTiers.length - 1]]
            : qualifiedTiers;

        /* Casamos por PRODUCT_ID, não por variant_id — assim qualquer variante
           do produto-brinde conta como "esse brinde já está no carrinho",
           preservando a escolha de variante feita pelo cliente. Fallback para
           variantId quando productId não estiver configurado (compat antiga). */
        const tierMatchKey = (tier) => tier.productId || ('v:' + tier.variantId);
        const itemMatchKey = (item, tiers) => {
          /* Tenta casar por product_id primeiro (preserva variante escolhida) */
          const byProduct = tiers.find(t => t.productId && t.productId === item.product_id);
          if (byProduct) return byProduct.productId;
          /* Fallback antigo: casar por variantId exato */
          const byVariant = tiers.find(t => !t.productId && t.variantId === item.variant_id);
          return byVariant ? ('v:' + byVariant.variantId) : null;
        };

        const targetKeys = new Set(targetTiers.map(tierMatchKey));

        /* ── Agrupa brindes existentes por tierKey para detectar duplicatas e
              brindes "órfãos" (sem tier correspondente). Cada tier qualificado
              deve ter EXATAMENTE 1 item no carrinho. ─────────────────────── */
        const giftsByKey = new Map(); // tierKey -> array de items
        const orphanGifts = [];        // items com _brinde_auto=true mas sem tier
        for (const item of currentGiftItems) {
          const k = itemMatchKey(item, this.tiers);
          if (k === null) { orphanGifts.push(item); continue; }
          if (!giftsByKey.has(k)) giftsByKey.set(k, []);
          giftsByKey.get(k).push(item);
        }

        const toRemoveItems = [];

        /* 1) Brindes órfãos (produto não está em nenhum tier) → remover todos. */
        toRemoveItems.push(...orphanGifts);

        /* 2) Brindes de tier NÃO qualificado → remover todos. */
        for (const [k, items] of giftsByKey) {
          if (!targetKeys.has(k)) toRemoveItems.push(...items);
        }

        /* 3) Brindes de tier qualificado mas com QUANTIDADE > 1 ou line.quantity > 1
              → manter só o primeiro com quantity=1, remover excedentes.
              Isso protege contra: race conditions, edits que duplicaram, manipulação
              direta do cart API e qualquer cenário em que o cliente acabou com mais
              de 1 brinde do mesmo tier. */
        for (const k of targetKeys) {
          const items = giftsByKey.get(k) || [];
          if (items.length === 0) continue;
          // Mantém o primeiro item, remove os demais (linhas duplicadas)
          for (let i = 1; i < items.length; i++) toRemoveItems.push(items[i]);
          // E garante que o primeiro tenha quantity=1 (se vier 2+, força volta a 1)
          const first = items[0];
          if (first.quantity > 1) {
            // Não adiciona ao toRemoveItems (que zera), trata separado abaixo
            first.__forceQty1 = true;
          }
        }

        /* ── Itens com _brinde_auto=true que precisam reduzir quantity a 1.
              Tratados separadamente do remove total para não eliminar o brinde. ── */
        const toForceQty1 = [];
        for (const items of giftsByKey.values()) {
          if (items[0] && items[0].__forceQty1) toForceQty1.push(items[0]);
        }

        /* ADD: tiers qualificados que NÃO têm nenhum item correspondente após
                 considerar o que sobra após as remoções. */
        const remainingKeys = new Set(
          [...giftsByKey.entries()]
            .filter(([k, items]) => targetKeys.has(k) && items.length > 0)
            .map(([k]) => k)
        );
        const toAddIds = targetTiers
          .filter(t => !remainingKeys.has(tierMatchKey(t)))
          .map(t => t.variantId);

        console.log('[BrindeAuto] Target tiers:', targetTiers.map(t => t.productTitle), '| To add:', toAddIds, '| To remove:', toRemoveItems.length, '| To force qty=1:', toForceQty1.length);

        if (toRemoveItems.length === 0 && toAddIds.length === 0 && toForceQty1.length === 0) {
          console.log('[BrindeAuto] No changes needed');
          return;
        }

        // Remove gifts that shouldn't be there + force qty=1 on duplicated lines
        if (toRemoveItems.length > 0 || toForceQty1.length > 0) {
          const updates = {};
          toRemoveItems.forEach(i => { updates[i.key] = 0; });
          toForceQty1.forEach(i => { updates[i.key] = 1; });
          await this._cartRequest('/cart/update.js', { updates });
          await new Promise(r => setTimeout(r, 400));
        }

        // Add gifts one at a time with pauses
        for (const id of toAddIds) {
          if (window.__cartBusy) break; // abort if user started a cart op
          console.log('[BrindeAuto] Adding gift variant:', id);
          const addResult = await this._cartRequest('/cart/add.js', {
            id, quantity: 1, properties: { [BRINDE_KEY]: BRINDE_VAL }
          });
          console.log('[BrindeAuto] Add result:', addResult ? 'success' : 'FAILED');
          await new Promise(r => setTimeout(r, 400));
        }

        // Refresh minicart UI
        await this._refreshMinicart();
        console.log('[BrindeAuto] Minicart refreshed');

      } catch (err) {
        console.warn('[BrindeAuto] Error:', err.message);
      } finally {
        this.processing = false;
      }
    }

    async _fetchCart() {
      // Global dedup: se outra instância (ex: versão do App) já iniciou
      // uma busca nos últimos 3s, reutiliza a mesma Promise sem nova requisição HTTP.
      const now = Date.now();
      if (window.__brindeCartPromise && (now - window.__brindeCartTimestamp) < 3000) {
        return window.__brindeCartPromise;
      }
      window.__brindeCartTimestamp = now;
      window.__brindeCartPromise = fetch('/cart.json', { credentials: 'same-origin' })
        .then(res => {
          if (res.status === 429) return null;
          if (!res.ok) return null;
          return res.json();
        })
        .catch(() => null)
        .finally(() => {
          // Libera cache após 3s para o próximo ciclo real
          setTimeout(() => { window.__brindeCartPromise = null; }, 3000);
        });
      return window.__brindeCartPromise;
    }

    async _cartRequest(url, data, retries = 1) {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const res = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });
          if (res.ok) return await res.json();
          if (attempt < retries) {
            console.log('[BrindeAuto] Cart request failed (' + res.status + '), retrying...');
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          return null;
        } catch {
          if (attempt < retries) {
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          return null;
        }
      }
      return null;
    }

    async _refreshMinicart() {
      try {
        const cart = await this._fetchCart(); // usa cache global — sem request extra
        if (!cart) return;

        document.querySelectorAll('.cart-count').forEach(el => {
          el.innerHTML = el.classList.contains('cart-count-drawer')
            ? `(${cart.item_count})`
            : (cart.item_count > 100 ? '~' : cart.item_count);
        });

        const headerPrice = document.querySelector('header-total-price');
        if (headerPrice?.updateTotal) headerPrice.updateTotal(cart);

        const freeShip = document.querySelector('free-ship-progress-bar');
        if (freeShip?.init) freeShip.init(cart.items_subtotal_price);

        // ── Cart PAGE: re-render the full cart section ──────────────────────
        // When on /cart, reload the main-cart-items section so brinde badges,
        // GRÁTIS label and auto-removal are reflected without a full page reload.
        const isCartPage = window.location.pathname === '/cart' ||
                           window.location.pathname.endsWith('/cart');
        if (isCartPage) {
          const cartPageEl = document.getElementById('main-cart-items');
          if (cartPageEl) {
            const sectionId = cartPageEl.dataset.id;
            if (sectionId) {
              const res = await fetch(`/cart?sections=${sectionId}`, { credentials: 'same-origin' });
              if (res.ok) {
                const sections = await res.json();
                const html = sections[sectionId];
                if (html) {
                  const parsed = new DOMParser().parseFromString(html, 'text/html');
                  const newSection = parsed.querySelector('#main-cart-items');
                  if (newSection) {
                    cartPageEl.innerHTML = newSection.innerHTML;
                    // Re-init lazy images after DOM replacement
                    if (typeof BlsLazyloadImg !== 'undefined') BlsLazyloadImg.init();
                  }
                }
              }
            }
          }
          return; // skip minicart-form update when on cart page
        }

        // ── Minicart DRAWER: re-render the drawer section ──────────────────
        const res = await fetch(`${window.location.pathname}?sections=minicart-form`, { credentials: 'same-origin' });
        if (!res.ok) return;
        const sections = await res.json();

        if (sections['minicart-form']) {
          const minicartForm = document.getElementById('minicart-form');
          if (minicartForm) {
            const parsed = new DOMParser().parseFromString(sections['minicart-form'], 'text/html');
            const newContent = parsed.querySelector('#minicart-form');
            if (newContent) minicartForm.innerHTML = newContent.innerHTML;

            // Restore the open addon panel.
            // Use CartUtils.openAddonId (set by capture-phase listener in cart-components.js)
            // instead of reading from the DOM — the DOM state was already cleared by
            // cart-components.js updateQuantity ~1500ms before this runs.
            if (window.CartUtils) {
              window.CartUtils.restoreOpenAddon();
            }

            // Reabre o painel lateral de recomendações de produtos.
            // Sem isso, o painel some "alguns segundos depois do add-to-cart"
            // porque o innerHTML acima destrói o estado .open do painel original
            // (e o <minicart-recommendations-beside> recém-criado não tem .open).
            // Reproduz a mesma lógica que cart-components.js executa após seu
            // próprio refresh — atrasa 800ms para o fetch interno do recommend
            // ter tempo de popular o conteúdo.
            const cartRecommend = document.querySelector('.cart-recommend');
            if (cartRecommend && !cartRecommend.classList.contains('hidden-recommend')) {
              if (cartRecommend.classList.contains('cart-recommend-custom')) {
                const cartUpsellItem  = document.querySelectorAll('.cart-upsell-item');
                const cartUpsellSlide = document.querySelectorAll('.swiper-cart-upsell .swiper-slide');
                if (cartUpsellItem.length > 0) {
                  setTimeout(() => cartRecommend.classList.add('open'), 800);
                } else if (cartUpsellSlide.length === 0) {
                  cartRecommend.classList.remove('block');
                  cartRecommend.classList.add('hidden');
                }
              } else {
                setTimeout(() => cartRecommend.classList.add('open'), 800);
              }
            }
          }
        }
      } catch (e) {
        // silent
      }

      const cartEl = document.querySelector('cart-notification');
      if (cartEl?.cartAction) cartEl.cartAction();
      if (typeof BlsLazyloadImg !== 'undefined') BlsLazyloadImg.init();
    }
  }

  window.BrindeAutoManager = BrindeAutoManager;
})();
